import addDays from 'date-fns/addDays'
import pick from 'lodash/pick'
import { action, comparer, computed, makeObservable } from 'mobx'

import { formatDate } from '@avantstay/backoffice-core'

import { FastTapeChartFiltersStore } from '@new/__global/components/FastTapeChart/external/FastTapeChartFiltersStore'

import { AvailableDatesRange, BaseStore } from '../__types'
import {
  BookingFilterIds,
  CustomFilterIds,
  HomeFilterIds,
  ViewStateFilterIds,
} from '../config/filtersConfig'
import { tapeChartGridSizingStore } from '../internal'

export class TapeChartFiltersApiStore {
  constructor(private getFilterStore: () => BaseStore['filterStore']) {
    makeObservable<TapeChartFiltersApiStore, 'rangeFilters'>(this, {
      rangeFilters: computed,
      unitsFilters: computed({ equals: comparer.structural }),
      bookingFilters: computed({ equals: comparer.structural }),
      toggleArrivalMode: action.bound,
    })
  }

  get filterStore() {
    return this.getFilterStore()
  }

  private get rangeFilters() {
    const rangeFiltersIds = [
      HomeFilterIds.Guests,
      HomeFilterIds.PriceRange,
      HomeFilterIds.BedroomsRange,
      HomeFilterIds.BathroomsRange,
      HomeFilterIds.AddressRadius,
    ]

    if (!this.filterStore?.filteringArgs) {
      return {}
    }

    // AvailableDates filter value needs to be destructed to match API query structure
    const availableDatesFilter: AvailableDatesRange | undefined =
      this.filterStore.filteringArgs[CustomFilterIds.AvailableDates]
    const guestsRangeFilter = this.filterStore.filteringArgs[HomeFilterIds.Guests]
    const priceRangeFilter = this.filterStore.filteringArgs[HomeFilterIds.PriceRange]
    const bedroomsRangeFilter = this.filterStore.filteringArgs[HomeFilterIds.BedroomsRange]
    const bathroomsRangeFilter = this.filterStore.filteringArgs[HomeFilterIds.BathroomsRange]

    const addressRadiusFilter = this.filterStore.filteringArgs[HomeFilterIds.AddressRadius]

    const guestsFilter = {
      minOccupancy: guestsRangeFilter?.from,
      maxOccupancy: guestsRangeFilter?.to,
    }

    const pricesFilter = {
      minPrice: priceRangeFilter?.minPrice,
      maxPrice: priceRangeFilter?.maxPrice,
    }

    const bedroomsFilter = {
      minBedrooms: bedroomsRangeFilter?.from,
      maxBedrooms: bedroomsRangeFilter?.to,
    }

    const bathroomsFilter = {
      minBathrooms: bathroomsRangeFilter?.from,
      maxBathrooms: bathroomsRangeFilter?.to,
    }

    const pointOfOrigin = {
      pointOfOrigin: {
        latitude: addressRadiusFilter?.latitude,
        longitude: addressRadiusFilter?.longitude,
      },
    }

    const address =
      addressRadiusFilter?.radius && addressRadiusFilter?.latitude && addressRadiusFilter?.longitude
        ? {
            addressRadius: {
              radius: addressRadiusFilter?.radius,
              ...pointOfOrigin,
            },
          }
        : undefined

    const additionalRangeFilters = {
      ...guestsFilter,
      ...pricesFilter,
      ...bedroomsFilter,
      ...bathroomsFilter,
      ...address,
    }

    return {
      rangeFiltersIds,
      availableDatesFilter,
      additionalRangeFilters,
    }
  }

  get unitsFilters() {
    const { rangeFiltersIds, availableDatesFilter, additionalRangeFilters } = this.rangeFilters

    const filters = pick(
      this.filterStore.filteringArgs,
      Object.values(HomeFilterIds).filter(filter => !rangeFiltersIds?.includes(filter)),
    )

    if (!availableDatesFilter) {
      return { ...filters, ...additionalRangeFilters }
    }

    return {
      ...filters,
      ...availableDatesFilter,
      ...additionalRangeFilters,
    }
  }

  get bookingFilters() {
    return pick(this.filterStore.filteringArgs, Object.values(BookingFilterIds))
  }

  toggleArrivalMode(value?: boolean) {
    const filterStore = this.filterStore as FastTapeChartFiltersStore

    filterStore?.computedRouteParams?.[ViewStateFilterIds.ArrivalsMode].push(value)
    const checkInDates = filterStore?.checkInExistsBetween

    const shouldResetCheckInDates =
      checkInDates?.start &&
      checkInDates?.end &&
      formatDate(checkInDates.start) === formatDate(new Date()) &&
      formatDate(checkInDates.end) === formatDate(addDays(new Date(), 1))

    if (!value && shouldResetCheckInDates) {
      filterStore.computedRouteParams?.[CustomFilterIds.CheckInExistsBetween].replace(undefined)
    }

    if (!filterStore.arrivalsMode && !!tapeChartGridSizingStore.isGridMaximised) {
      tapeChartGridSizingStore.setGridSizing(false)
    }
  }
}
